import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  Badge,
  Button,
  confirm,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ExternalLinkIcon, FileTextIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useMemo, useState } from "react";
import type {
  PayoutListItemDTO,
  ProductionCostLineDTO,
} from "../../domain/contracts";
import { formatDocumentMoney } from "@/shared/lib/performance-document-currency";
import { performanceApi } from "../api/client";
import {
  useDeletePayout,
  useEventPayoutsQuery,
} from "../api/queries";
import { CreateProductionPayoutDialog } from "./CreateProductionPayoutDialog";
import { PerformanceBrandPrintMenu } from "./PerformanceBrandPrintMenu";

const STATUS_LABELS = {
  draft: "ຮ່າງ",
  pending_approval: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  paid: "ຈ່າຍແລ້ວ",
  cancelled: "ຍົກເລີກ",
} as const;

const STATUS_VARIANTS = {
  draft: "secondary",
  pending_approval: "secondary",
  approved: "default",
  paid: "outline",
  cancelled: "destructive",
} as const;

function formatMoney(value: number) {
  return formatDocumentMoney(value, "LAK");
}

type EventProductionPayoutRowProps = {
  payout: PayoutListItemDTO;
  eventId: string;
  canDelete: boolean;
  onOpen: () => void;
};

function EventProductionPayoutRow({
  payout,
  eventId,
  canDelete,
  onOpen,
}: EventProductionPayoutRowProps) {
  const deletePayout = useDeletePayout(eventId);
  const isCancelled = payout.status === "cancelled";
  const canPrint = payout.status !== "cancelled";
  const printPreview =
    payout.status === "draft" || payout.status === "pending_approval";

  return (
    <div
      className={
        isCancelled
          ? "flex flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed px-4 py-3"
          : "flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/20 px-4 py-3"
      }
    >
      <button
        type="button"
        className="min-w-0 text-left hover:underline disabled:pointer-events-none disabled:no-underline"
        disabled={isCancelled}
        onClick={onOpen}
      >
        <p className="font-semibold text-sm">{payout.payoutNo}</p>
        <p className="mt-0.5 text-muted-foreground text-xs">
          Prop / ວັດສະດຸ · {formatMoney(payout.totalAmount)}
        </p>
      </button>

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={STATUS_VARIANTS[payout.status] ?? "secondary"}>
          {STATUS_LABELS[payout.status] ?? payout.status}
        </Badge>
        {!isCancelled ? (
          <Button size="sm" variant="outline" className="h-8" onClick={onOpen}>
            <ExternalLinkIcon className="size-4" /> ເປີດ
          </Button>
        ) : null}
        {canPrint ? (
          <PerformanceBrandPrintMenu
            className="h-8 gap-1 px-2"
            preview={printPreview}
            buildPrintUrl={(lang) =>
              performanceApi.payouts.printUrl(payout.id, lang)
            }
          />
        ) : null}
        {canDelete && isCancelled ? (
          <Button
            size="sm"
            variant="ghost"
            className="h-8 text-destructive hover:bg-destructive/10"
            disabled={deletePayout.isPending}
            onClick={async () => {
              const ok = await confirm({
                title: "ລຶບໃບເບີກຈ່າຍ",
                description:
                  "ໃບເບີກຈ່າຍທີ່ຍົກເລີກແລ້ວຈະຖືກລຶບຖາວອນ — ກູ້ຄືນບໍ່ໄດ້",
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) deletePayout.mutate(payout.id);
            }}
          >
            <Trash2Icon className="size-4" /> ລຶບ
          </Button>
        ) : null}
      </div>
    </div>
  );
}

type EventProductionPayoutSectionProps = {
  eventId: string;
  costLines: ProductionCostLineDTO[];
};

export function EventProductionPayoutSection({
  eventId,
  costLines,
}: EventProductionPayoutSectionProps) {
  const nav = useNavigate({ from: "/app/performance/events/$id" });
  const canCreate = useActionPermission(["performance-payouts:create"]);
  const canDelete = useActionPermission(["performance-payouts:delete"]);
  const payoutsQuery = useEventPayoutsQuery(eventId);
  const [createOpen, setCreateOpen] = useState(false);

  const productionPayouts = useMemo(
    () => (payoutsQuery.data ?? []).filter((item) => item.isProductionPayout),
    [payoutsQuery.data],
  );
  const activePayouts = productionPayouts.filter(
    (item) => item.status !== "cancelled",
  );
  const cancelledPayouts = productionPayouts.filter(
    (item) => item.status === "cancelled",
  );
  const hasAvailableLines = costLines.some(
    (row) => row.status === "planned" && !row.linkedPayoutId,
  );

  const openPayout = (payoutId: string) =>
    nav({
      to: "/app/performance/payouts/$id",
      params: { id: payoutId },
    });

  return (
    <>
      <div className="rounded-lg border bg-muted/10 p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <FileTextIcon className="size-4 text-primary" />
            <div>
              <p className="font-semibold text-sm">ໃບເບີກຈ່າຍ Prop / ວັດສະດຸ</p>
              <p className="text-muted-foreground text-xs">
                ເລືອກລາຍການຕົ້ນທຸນ → ສ້າງໃບເບີກຈ່າຍ → ອະນຸມັດ → ຈ່າຍ → ພິມ PDF
              </p>
            </div>
          </div>
          {canCreate ? (
            <Button
              size="sm"
              variant="outline"
              disabled={!hasAvailableLines}
              onClick={() => setCreateOpen(true)}
            >
              <PlusIcon className="size-4" />
              ສ້າງໃບເບີກຈ່າຍ
            </Button>
          ) : null}
        </div>

        {payoutsQuery.isLoading ? (
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
        ) : activePayouts.length === 0 && cancelledPayouts.length === 0 ? (
          <Empty className="border border-dashed py-6">
            <EmptyHeader>
              <EmptyTitle>ຍັງບໍ່ມີໃບເບີກຈ່າຍ</EmptyTitle>
              <EmptyDescription>
                ເລືອກລາຍການຕົ້ນທຸນທີ່ສະຖານະ «ວາງແຜນ» ເພື່ອສ້າງໃບເບີກຈ່າຍ
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="space-y-2">
            {activePayouts.map((payout) => (
              <EventProductionPayoutRow
                key={payout.id}
                payout={payout}
                eventId={eventId}
                canDelete={canDelete}
                onOpen={() => openPayout(payout.id)}
              />
            ))}
            {cancelledPayouts.map((payout) => (
              <EventProductionPayoutRow
                key={payout.id}
                payout={payout}
                eventId={eventId}
                canDelete={canDelete}
                onOpen={() => openPayout(payout.id)}
              />
            ))}
          </div>
        )}
      </div>

      <CreateProductionPayoutDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        eventId={eventId}
        costLines={costLines}
      />
    </>
  );
}
